// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * MultiSelectControl - Checkbox list for multi-select settings.
 *
 * Fetches pack metadata from endpoint and displays checkbox list.
 * Handles null (all selected) vs explicit selection array.
 *
 * COMPREHENSIVE TEST IDs for excellent testing DX:
 * - All interactive elements have data-testid
 * - Pack items, checkboxes, buttons all testable
 */

import { useState, useEffect } from 'react';
import { ControlWrapper } from './BaseControl';
import { logger } from '../../../lib/logger';
import type { BaseControlProps } from './BaseControl';

interface PackMetadata {
  index: number;
  name: string;
  description: string;
  content_count: number;
  official: boolean;
  tags: string[];
}

export interface MultiSelectControlProps extends BaseControlProps<number[] | null> {
  config?: {
    pack_metadata_endpoint?: string;
    min_selections?: number;
    max_selections?: number;
  };
  gameId?: string;
}

export function MultiSelectControl({
  label,
  value,
  description,
  error,
  onChange,
  disabled = false,
  config,
  gameId,
}: MultiSelectControlProps) {
  const [packs, setPacks] = useState<PackMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const packMetadataEndpoint = config?.pack_metadata_endpoint;
  const baseTestId = `multi-select-${label.toLowerCase().replace(/\s+/g, '-')}`;

  // Fetch pack metadata on mount
  useEffect(() => {
    if (!packMetadataEndpoint) {
      setFetchError('No pack metadata endpoint configured');
      setLoading(false);
      return;
    }

    const fetchPacks = async () => {
      try {
        const response = await fetch(packMetadataEndpoint);
        if (!response.ok) {
          throw new Error(`Failed to fetch packs: ${response.statusText}`);
        }
        const data = await response.json();
        setPacks(data.packs || []);
        logger.debug('packs_fetched', {
          gameId,
          count: data.packs?.length || 0,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setFetchError(message);
        logger.error('packs_fetch_failed', { gameId, error: message });
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, [packMetadataEndpoint, gameId]);

  // Determine if a pack is selected
  const isPackSelected = (packIndex: number): boolean => {
    if (value === null || value === undefined) return true; // null/undefined = all selected
    return value.includes(packIndex);
  };

  // Toggle individual pack
  const togglePack = (packIndex: number) => {
    if (disabled) return;

    if (value === null) {
      // Deselecting from "all" - create list of all EXCEPT this one
      const newSelection = packs.map(p => p.index).filter(idx => idx !== packIndex);
      onChange(newSelection);
    } else {
      if (value.includes(packIndex)) {
        // Deselect
        const newSelection = value.filter(idx => idx !== packIndex);
        // Keep as array even if empty (validation will catch it)
        onChange(newSelection);
      } else {
        // Select
        onChange([...value, packIndex]);
      }
    }

    logger.debug('pack_toggled', {
      gameId,
      packIndex,
      selected: !isPackSelected(packIndex),
    });
  };

  // Toggle all packs
  const toggleAll = () => {
    if (disabled) return;

    if (value === null || value === undefined || value.length === packs.length) {
      // Deselect all (empty array triggers validation error)
      onChange([]);
      logger.debug('all_packs_deselected', { gameId });
    } else {
      // Select all (null = all)
      onChange(null);
      logger.debug('all_packs_selected', { gameId });
    }
  };

  // Loading state
  if (loading) {
    return (
      <ControlWrapper label={label} description={description} error={error} testId={baseTestId}>
        <p className="text-xs text-gray-400" data-testid={`${baseTestId}-loading`}>
          Loading packs...
        </p>
      </ControlWrapper>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <ControlWrapper label={label} description={description} error={error} testId={baseTestId}>
        <p className="text-xs text-red-400" data-testid={`${baseTestId}-fetch-error`}>
          {fetchError}
        </p>
      </ControlWrapper>
    );
  }

  return (
    <ControlWrapper label={label} description={description} error={error} testId={baseTestId}>
      {/* Select All / Deselect All Button */}
      <div className="mb-2">
        <button
          type="button"
          onClick={toggleAll}
          disabled={disabled}
          className="text-xs text-purple-400 hover:text-purple-300 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`${baseTestId}-toggle-all`}
        >
          {value === null || value === undefined || value.length === packs.length
            ? 'Deselect All'
            : 'Select All'}
        </button>
      </div>

      {/* Pack Checkboxes */}
      <div
        className="space-y-2 max-h-64 overflow-y-auto border border-white/20 rounded-lg p-3"
        data-testid={`${baseTestId}-pack-list`}
      >
        {packs.map(pack => {
          const selected = isPackSelected(pack.index);
          const packTestId = `${baseTestId}-pack-${pack.index}`;

          return (
            <label
              key={pack.index}
              className={`flex items-start gap-3 p-2 rounded hover:bg-white/5 cursor-pointer ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              data-testid={packTestId}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={() => togglePack(pack.index)}
                disabled={disabled}
                className="mt-0.5 h-4 w-4 rounded border-gray-600 bg-gray-700 text-purple-600 focus:ring-2 focus:ring-purple-500"
                data-testid={`${packTestId}-checkbox`}
              />

              <div className="flex-1">
                <div className="text-sm font-medium" data-testid={`${packTestId}-name`}>
                  {pack.name}
                  {!pack.official && (
                    <span
                      className="ml-2 text-xs text-gray-400"
                      data-testid={`${packTestId}-community-badge`}
                    >
                      (Community)
                    </span>
                  )}
                </div>

                {pack.description && (
                  <div
                    className="text-xs text-gray-400 mt-0.5"
                    data-testid={`${packTestId}-description`}
                  >
                    {pack.description}
                  </div>
                )}

                <div className="text-xs text-gray-500 mt-1" data-testid={`${packTestId}-count`}>
                  {pack.content_count} {pack.content_count === 1 ? 'item' : 'items'}
                </div>

                {pack.tags.length > 0 && (
                  <div className="flex gap-1 mt-1" data-testid={`${packTestId}-tags`}>
                    {pack.tags.map(tag => (
                      <span
                        key={tag}
                        className="text-xs px-1.5 py-0.5 bg-gray-700 rounded"
                        data-testid={`${packTestId}-tag-${tag}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </label>
          );
        })}

        {packs.length === 0 && (
          <p className="text-sm text-gray-400" data-testid={`${baseTestId}-no-packs`}>
            No packs available
          </p>
        )}
      </div>

      {/* Selection Summary */}
      <div className="text-xs text-gray-400 mt-2" data-testid={`${baseTestId}-summary`}>
        {value === null || value === undefined
          ? `All ${packs.length} packs selected`
          : `${value.length} of ${packs.length} packs selected`}
      </div>
    </ControlWrapper>
  );
}
